import { Editor, Node, Range, Text } from '../../core';
import type { DictationChange, DictationCheckItem } from './types';
import { locate } from './locate';
import { autoApply } from './actions';

interface Snapshot {
  items: readonly DictationCheckItem[];
  skippedCount: number;
  openCount: number;
  resolved: readonly DictationCheckItem[];
  listHidden?: boolean;
  focusedId?: string;
  /** The leaf of the focused mark that opened the popover. A mark can span several leaves. */
  focusedAnchor?: string;
}
const EMPTY: Snapshot = { items: [], resolved: [], openCount: 0, skippedCount: 0 };
const stores = new WeakMap<Editor, ReturnType<typeof createStore>>();

function createStore(editor: Editor) {
  let snapshot = EMPTY;
  let actionDepth = 0;
  let historyRecorder = () => {};
  const listeners = new Set<() => void>();
  function publish(next: Snapshot) {
    [...snapshot.items, ...snapshot.resolved].forEach((item) => {
      if (![...next.items, ...next.resolved].includes(item)) item.rangeRef.unref();
    });
    snapshot = { ...next, openCount: next.items.filter((item) => item.status === 'open').length };
    listeners.forEach((listener) => listener());
  }
  return {
    getSnapshot: () => snapshot,
    setHistoryRecorder: (recorder: () => void) => {
      historyRecorder = recorder;
    },
    recordHistory: () => historyRecorder(),
    isApplyingAction: () => actionDepth > 0,
    withAction: (action: () => void) => {
      actionDepth += 1;
      try {
        action();
      } finally {
        actionDepth -= 1;
      }
    },
    focus: (focusedId?: string, focusedAnchor?: string) => {
      if (snapshot.focusedId !== focusedId || snapshot.focusedAnchor !== focusedAnchor) {
        publish({ ...snapshot, focusedId, focusedAnchor });
      }
    },
    update: () => publish({ ...snapshot }),
    toggleList: () => publish({ ...snapshot, listHidden: !snapshot.listHidden }),
    resolve: (id: string, status: NonNullable<DictationCheckItem['lastResolvedStatus']>) => {
      const item = snapshot.items.find((candidate) => candidate.change.id === id);
      if (!item) return;
      item.lastResolvedStatus = status;
      item.status = status;
      publish({
        ...snapshot,
        items: snapshot.items.filter((candidate) => candidate !== item),
        resolved: [...snapshot.resolved, item],
        focusedId: snapshot.focusedId === id ? undefined : snapshot.focusedId,
        focusedAnchor: snapshot.focusedId === id ? undefined : snapshot.focusedAnchor,
      });
    },
    reopen: (id: string) => {
      const item = snapshot.resolved.find((candidate) => candidate.change.id === id);
      if (!item) return;
      item.status = 'open';
      publish({
        ...snapshot,
        items: [...snapshot.items, item],
        resolved: snapshot.resolved.filter((candidate) => candidate !== item),
      });
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    clear: () => {
      if (snapshot !== EMPTY) publish(EMPTY);
    },
    replace: (changes: readonly DictationChange[]) => {
      const items: DictationCheckItem[] = [];
      let skippedCount = 0;
      for (const change of changes) {
        const range = locate(editor, change);
        if (
          !range ||
          items.some(
            (item) =>
              item.change.id === change.id ||
              (item.rangeRef.current && rangesOverlap(item.rangeRef.current, range))
          )
        ) {
          skippedCount += 1;
          continue;
        }
        items.push({
          change,
          status: 'open',
          rangeRef: Editor.rangeRef(editor, range, { affinity: 'inward' }),
          replacedText: change.original,
          originalLeaves: [...Editor.nodes(editor, { at: range, match: Text.isText })].flatMap(
            ([node, path]) => {
              if (!Text.isText(node)) return [];
              const intersection = Range.intersection(range, {
                anchor: { path, offset: 0 },
                focus: { path, offset: node.text.length },
              });
              return intersection
                ? [
                    {
                      ...node,
                      text: node.text.slice(
                        Range.start(intersection).offset,
                        Range.end(intersection).offset
                      ),
                    },
                  ]
                : [];
            }
          ),
          formatting: (() => {
            const { text: _text, ...formatting } = Node.leaf(editor, Range.start(range).path);
            return formatting;
          })(),
        });
      }
      publish({ items, resolved: [], openCount: items.length, skippedCount });
      autoApply(editor);
    },
  };
}

export function getDictationCheckStore(editor: Editor) {
  let store = stores.get(editor);
  if (!store) {
    store = createStore(editor);
    stores.set(editor, store);
  }
  return store;
}

function rangesOverlap(first: Range, second: Range): boolean {
  const intersection = Range.intersection(first, second);
  return (
    !!intersection &&
    (!Range.isCollapsed(intersection) || Range.isCollapsed(first) || Range.isCollapsed(second))
  );
}
